/**
 * Article Image helpers shared by the editor (extensions/ArticleImage*.ts),
 * the reading view (ArticleBody) and the article form.
 *
 * Body markup contract (srg-backend ADR 0004), kept in sync with srg-backend
 * api/services/help/helpers/sanitize.ts:
 *
 *   <figure data-placement="left|right|center" data-size="small|medium|full">
 *     <img data-image-id="<uuid>" alt="Caption or empty" src="<SAS url, responses only>">
 *     <figcaption>Caption</figcaption>   (omitted when there is no Caption)
 *   </figure>
 */
import type { ImagePlacement, ImageSize } from '@app-types';
import { getApiErrorMessage, getApiErrorStatus } from '@utils/apiError';

export const IMAGE_PLACEMENTS: readonly ImagePlacement[] = ['left', 'center', 'right'];
export const IMAGE_SIZES: readonly ImageSize[] = ['small', 'medium', 'full'];

/** New images sit on their own line until the Admin chooses to wrap text. */
export const DEFAULT_IMAGE_PLACEMENT: ImagePlacement = 'center';

/** Mirrors the backend limit; the server stays authoritative. */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

/** Mirrors the backend magic-byte allow-list. SVG is deliberately absent. */
export const ALLOWED_IMAGE_TYPES: readonly string[] = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

const IMAGE_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const IMAGE_UPLOAD_ERROR_CODES = {
  tooLarge: 'HELP_IMAGE_TOO_LARGE',
  unsupportedType: 'HELP_IMAGE_UNSUPPORTED_TYPE'
} as const;

export const IMAGE_UPLOAD_MESSAGES = {
  tooLarge: 'The image is too large. The maximum size is 10 MB.',
  unsupportedType: 'This image type is not supported. Use PNG, JPEG, WebP or GIF.',
  serverError: 'The image could not be uploaded because of a server error. Please try again.',
  copyFailed: 'A pasted image could not be copied into this article and was removed.'
} as const;

const HTTP_SERVER_ERROR = 500;

interface ApiErrorCodeShape {
  response?: {
    data?: {
      error?: { code?: string } | string;
    };
  };
}

export const isImagePlacement = (value: unknown): value is ImagePlacement =>
  typeof value === 'string' && (IMAGE_PLACEMENTS as readonly string[]).includes(value);

export const isImageSize = (value: unknown): value is ImageSize =>
  typeof value === 'string' && (IMAGE_SIZES as readonly string[]).includes(value);

/** Article Image ids are UUIDs issued by the upload endpoint. */
export const isArticleImageId = (value: unknown): value is string =>
  typeof value === 'string' && IMAGE_ID_PATTERN.test(value);

/** Azure Blob Storage, where the backend keeps Article Images. */
const BLOB_STORAGE_HOST_SUFFIX = '.blob.core.windows.net';
/** The local storage emulator (Azurite) used in development. */
const LOCAL_STORAGE_HOSTS = ['127.0.0.1', 'localhost'];

/**
 * True when `src` is a URL the backend could have issued for this image: the
 * reading copy `<imageId>.webp`, over https on Azure Blob Storage (or the local
 * emulator). The server always replaces src, so this only guards rendering
 * against markup that did not come from it.
 */
export const isArticleImageSource = (src: string, imageId: string | null): boolean => {
  if (!imageId || !isArticleImageId(imageId)) return false;

  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return false;
  }

  const isBlobStorage = url.protocol === 'https:' && url.hostname.endsWith(BLOB_STORAGE_HOST_SUFFIX);
  const isLocalEmulator = LOCAL_STORAGE_HOSTS.includes(url.hostname) && ['http:', 'https:'].includes(url.protocol);
  return (isBlobStorage || isLocalEmulator) && url.pathname.endsWith(`/${imageId}.webp`);
};

/** Left/Right default to Medium so text has room to wrap; Center fills the column. */
export const getDefaultImageSize = (placement: ImagePlacement): ImageSize =>
  placement === 'center' ? 'full' : 'medium';

/**
 * Client-side pre-check so an obviously invalid file fails fast without a
 * round trip. Returns the toast message, or null when the file may be sent.
 * An empty MIME type is let through: the server checks magic bytes anyway.
 */
export const getImageFileError = (file: Blob): string | null => {
  if (file.type && !ALLOWED_IMAGE_TYPES.includes(file.type)) return IMAGE_UPLOAD_MESSAGES.unsupportedType;
  if (file.size > MAX_IMAGE_BYTES) return IMAGE_UPLOAD_MESSAGES.tooLarge;
  return null;
};

const getApiErrorCode = (error: unknown): string | undefined => {
  const nested = (error as ApiErrorCodeShape | undefined)?.response?.data?.error;
  return nested && typeof nested === 'object' ? nested.code : undefined;
};

/**
 * Maps a failed upload to the reason shown to the Admin: too large,
 * unsupported type, or server error. Other 4xx responses (e.g. a missing
 * file) carry the backend's own message.
 */
export const getImageUploadErrorMessage = (error: unknown): string => {
  const code = getApiErrorCode(error);
  if (code === IMAGE_UPLOAD_ERROR_CODES.tooLarge) return IMAGE_UPLOAD_MESSAGES.tooLarge;
  if (code === IMAGE_UPLOAD_ERROR_CODES.unsupportedType) return IMAGE_UPLOAD_MESSAGES.unsupportedType;

  const status = getApiErrorStatus(error);
  if (status !== undefined && status < HTTP_SERVER_ERROR) {
    return getApiErrorMessage(error, IMAGE_UPLOAD_MESSAGES.serverError);
  }
  return IMAGE_UPLOAD_MESSAGES.serverError;
};
